import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  CreditCard, 
  TrendingUp, 
  AlertCircle, 
  Check, 
  Users, 
  Database, 
  Mail, 
  Shield, 
  Crown,
  Clock,
  BarChart3,
  Calendar
} from 'lucide-react';
import { buildApiUrl } from '../config/api';

interface PlanPricing {
  monthly: {
    price_cents: number;
    price_dollars: number;
  };
  annual: {
    price_cents: number;
    price_dollars: number;
    monthly_equivalent_cents: number;
    monthly_equivalent_dollars: number;
  };
}

interface PlanInfo {
  name: string;
  events_limit: number;
  max_projects: number;
  data_retention_days: number;
  features: string[];
  pricing: PlanPricing;
}

interface BillingData {
  organization: {
    id: number;
    name: string;
    plan: string;
    billing_cycle: string;
    billing_email: string;
    events_count: number;
    plan_limit: number;
    events_remaining: number;
    usage_percentage: number;
    trial_active: boolean;
    trial_expired: boolean;
    trial_days_remaining: number;
    trial_ends_at: string;
    over_limit: boolean;
    approaching_limit: boolean;
    can_track_events: boolean;
  };
  plans: {
    [key: string]: PlanInfo;
  };
  trial_days: number;
}

interface BillingProps {
  currentProject: any;
  token: string;
  user: any;
}

const planIcons = {
  pro: TrendingUp,
  business: BarChart3,
  enterprise: Crown
};

const planColors = {
  pro: 'bg-blue-100 text-blue-800 border-blue-300',
  business: 'bg-purple-100 text-purple-800 border-purple-300',
  enterprise: 'bg-gold-100 text-gold-800 border-gold-300'
};

export default function Billing({ currentProject, token, user }: BillingProps) {
  const [billingData, setBillingData] = useState<BillingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [upgrading, setUpgrading] = useState<string | null>(null);

  const currentOrg = user?.organizations?.[0];
  const isAdmin = currentOrg?.role === 'admin';

  useEffect(() => {
    if (currentOrg) {
      fetchBillingData();
    }
  }, [currentOrg, token]);

  const fetchBillingData = async () => {
    if (!currentOrg) return;
    
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(`api/v1/organizations/${currentOrg.id}/billing`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch billing data');
      }

      const data = await response.json();
      setBillingData(data.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planName: string) => {
    if (!currentOrg || !billingData) return;
    
    try {
      setUpgrading(planName);
      
      // If user is on trial, use checkout session for first-time payment
      if (billingData.organization.trial_active) {
        const response = await fetch(buildApiUrl(`api/v1/organizations/${currentOrg.id}/billing/checkout`), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ plan: planName })
        });

        if (!response.ok) {
          throw new Error('Failed to create checkout session');
        }

        const data = await response.json();
        window.location.href = data.data.checkout_url;
      } else {
        // For existing customers, use portal for plan changes
        const response = await fetch(buildApiUrl(`api/v1/organizations/${currentOrg.id}/billing/portal`), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (!response.ok) {
          throw new Error('Failed to create portal session');
        }

        const data = await response.json();
        window.location.href = data.data.portal_url;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start upgrade process');
    } finally {
      setUpgrading(null);
    }
  };

  const handleManageBilling = async () => {
    if (!currentOrg || !billingData) return;
    
    try {
      setUpgrading('portal');
      const response = await fetch(buildApiUrl(`api/v1/organizations/${currentOrg.id}/billing/portal`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to create portal session');
      }

      const data = await response.json();
      window.location.href = data.data.portal_url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open billing portal');
    } finally {
      setUpgrading(null);
    }
  };

  const formatNumber = (num: number | null | undefined) => {
    if (num == null || num === undefined) {
      return '0';
    }
    if (num === Infinity) {
      return '∞';
    }
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  if (loading) {
    return (
      <div className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold mb-8">Billing</h1>
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold mb-8">Billing</h1>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Admin Access Required
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Only organization administrators can view and manage billing settings.
              </p>
              <p className="text-sm text-muted-foreground">
                Contact your organization admin to make changes to your billing plan or payment methods.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold mb-8">Billing</h1>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertCircle className="h-5 w-5" />
                Error
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={fetchBillingData}>Try Again</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!billingData) {
    return (
      <div className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center mb-6 text-neutral-800">
            <CreditCard className="h-6 w-6 mr-1" />
            <h1 className="text-2xl font-bold flex items-center">Billing</h1>
          </div>
          <Card>
            <CardContent className="pt-6">
              <p className="text-muted-foreground">No billing data available</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const { organization, plans } = billingData;
  const currentPlan = plans[organization.plan];
  const CurrentPlanIcon = planIcons[organization.plan as keyof typeof planIcons];
  const isAnnual = organization.billing_cycle === 'annual';
  
  // Get the current price based on billing cycle
  const getCurrentPlanPrice = () => {
    if (!currentPlan?.pricing) return null;
    if (isAnnual) {
      return {
        amount: currentPlan.pricing.annual.monthly_equivalent_dollars,
        period: '/mo',
        billedAs: `$${currentPlan.pricing.annual.price_dollars.toLocaleString()}/year`
      };
    }
    return {
      amount: currentPlan.pricing.monthly.price_dollars,
      period: '/mo',
      billedAs: null
    };
  };
  
  const currentPrice = getCurrentPlanPrice();

  return (
    <div className="flex-1 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center mb-6 text-neutral-800">
          <CreditCard className="h-6 w-6 mr-1" />
          <h1 className="text-2xl font-bold flex items-center">Billing</h1>
        </div>
        
        {/* Trial Banner */}
        {organization.trial_active && currentPlan && (
          <Card 
            className="mb-6 border-orange-200 bg-orange-50"
            style={{
              opacity: 0,
              animation: 'cardFadeIn 500ms cubic-bezier(0.16, 1, 0.3, 1) forwards',
            }}
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-orange-600" />
                <div>
                  <p className="text-orange-800 font-medium">
                    You're on a free trial of the {currentPlan.name} plan —{' '}
                    {organization.trial_days_remaining} days left or up to{' '}
                    {formatNumber(250000 - organization.events_count)} more events in your 250K trial.
                  </p>
                  <p className="text-orange-700 text-sm mt-1">
                    After trial: Get {currentPlan.events_limit === null || currentPlan.events_limit === Infinity ? 'unlimited' : formatNumber(currentPlan.events_limit)} events per month
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Current Plan Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Current Plan */}
          <Card 
            className="card-premium-hover"
            style={{
              opacity: 0,
              animation: 'cardFadeIn 500ms cubic-bezier(0.16, 1, 0.3, 1) 100ms forwards',
            }}
          >
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <CurrentPlanIcon className="h-5 w-5" />
                Current Plan
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge className={`${planColors[organization.plan as keyof typeof planColors]} px-3 py-1`}>
                      {currentPlan?.name || organization.plan}
                    </Badge>
                    {isAnnual && (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        <Calendar className="h-3 w-3 mr-1" />
                        Annual
                      </Badge>
                    )}
                  </div>
                </div>
                
                {currentPrice && currentPrice.amount > 0 && (
                  <div className="space-y-1">
                    <span className="text-2xl font-bold">${currentPrice.amount}{currentPrice.period}</span>
                    {currentPrice.billedAs && (
                      <p className="text-xs text-muted-foreground">
                        Billed as {currentPrice.billedAs}
                      </p>
                    )}
                  </div>
                )}
                
                {organization.trial_active && (
                  <div className="flex items-center gap-2 text-orange-600">
                    <Clock className="h-4 w-4" />
                    <span className="text-sm">
                      {organization.trial_days_remaining} days remaining in trial
                    </span>
                  </div>
                )}
                
                {organization.billing_email && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    {organization.billing_email}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Usage */}
          <Card 
            className="card-premium-hover"
            style={{
              opacity: 0,
              animation: 'cardFadeIn 500ms cubic-bezier(0.16, 1, 0.3, 1) 200ms forwards',
            }}
          >
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Usage
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Events Used</span>
                  <span className="font-medium">
                    {formatNumber(organization.events_count)} / {
                      organization.plan_limit === Infinity ? '∞' : formatNumber(organization.plan_limit)
                    }
                  </span>
                </div>
                
                {organization.plan_limit !== Infinity && (
                  <div className="space-y-2">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full" 
                        style={{ width: `${organization.usage_percentage}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{organization.usage_percentage}% used</span>
                      <span>{formatNumber(organization.events_remaining)} remaining</span>
                    </div>
                  </div>
                )}
                
                {organization.approaching_limit && (
                  <div className="flex items-center gap-2 text-orange-600">
                    <AlertCircle className="h-4 w-4" />
                    <span className="text-sm">Approaching limit</span>
                  </div>
                )}
                
                {organization.over_limit && (
                  <div className="flex items-center gap-2 text-red-600">
                    <AlertCircle className="h-4 w-4" />
                    <span className="text-sm">Over limit</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Status */}
          <Card 
            className="card-premium-hover"
            style={{
              opacity: 0,
              animation: 'cardFadeIn 500ms cubic-bezier(0.16, 1, 0.3, 1) 300ms forwards',
            }}
          >
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Event Tracking</span>
                  <Badge variant={organization.can_track_events ? 'default' : 'destructive'}>
                    {organization.can_track_events ? 'Active' : 'Paused'}
                  </Badge>
                </div>
                
                {organization.trial_expired && (
                  <div className="flex items-center gap-2 text-red-600">
                    <AlertCircle className="h-4 w-4" />
                    <span className="text-sm">Trial expired</span>
                  </div>
                )}
                
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="h-4 w-4" />
                  {user.organizations[0].projects?.length || 0} projects
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Billing Management */}
        <Card 
          className="mb-8 card-premium-hover"
          style={{
            opacity: 0,
            animation: 'cardFadeIn 500ms cubic-bezier(0.16, 1, 0.3, 1) 400ms forwards',
          }}
        >
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Manage Billing
            </CardTitle>
            <CardDescription>
              {organization.trial_active 
                ? "Add a payment method and manage your billing preferences"
                : "Update your payment method, view invoices, and manage your subscription"
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Button 
                onClick={handleManageBilling}
                disabled={upgrading === 'portal'}
                className="flex items-center gap-2"
              >
                <CreditCard className="h-4 w-4" />
                {upgrading === 'portal' ? 'Opening Portal...' : 
                 organization.trial_active ? 'Manage Billing' : 'Manage Billing & Payment'}
              </Button>
              <div className="text-sm text-muted-foreground">
                Securely manage your billing through Stripe
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Available Plans */}
        <Card
          style={{
            opacity: 0,
            animation: 'cardFadeIn 500ms cubic-bezier(0.16, 1, 0.3, 1) 500ms forwards',
          }}
        >
          <CardHeader>
            <CardTitle>Available Plans</CardTitle>
            <CardDescription>
              Choose the plan that best fits your needs. You can upgrade or downgrade at any time.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(plans)
                .filter(([planKey]) => planKey !== organization.plan)
                .map(([planKey, plan], index) => {
                const PlanIcon = planIcons[planKey as keyof typeof planIcons];
                const monthlyPrice = plan.pricing?.monthly?.price_dollars || 0;
                const annualMonthlyPrice = plan.pricing?.annual?.monthly_equivalent_dollars || 0;
                const annualTotalPrice = plan.pricing?.annual?.price_dollars || 0;
                
                return (
                  <Card 
                    key={planKey} 
                    className="relative h-full flex flex-col card-premium-hover"
                    style={{
                      opacity: 0,
                      animation: `cardFadeIn 500ms cubic-bezier(0.16, 1, 0.3, 1) ${600 + index * 100}ms forwards`,
                    }}
                  >
                    <CardHeader className="text-center pb-4">
                      <CardTitle className="flex items-center justify-center gap-2 h-8">
                        <PlanIcon className="h-5 w-5" />
                        {plan.name}
                      </CardTitle>
                      {monthlyPrice > 0 ? (
                        <div className="space-y-2">
                          <div className="text-3xl font-bold">
                            ${monthlyPrice}
                            <span className="text-sm font-normal text-muted-foreground">/month</span>
                          </div>
                          {annualMonthlyPrice > 0 && (
                            <div className="text-sm text-green-600">
                              or ${annualMonthlyPrice}/mo billed annually
                              <span className="block text-xs text-muted-foreground">
                                (${annualTotalPrice.toLocaleString()}/year - save 2 months!)
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-3xl font-bold h-12 flex items-center justify-center">
                          Contact Us
                        </div>
                      )}
                    </CardHeader>
                    
                    <CardContent className="flex flex-col flex-1">
                      <div className="text-center mb-4">
                        <div className="text-lg font-semibold">
                          {plan.events_limit === null || plan.events_limit === Infinity ? 'Unlimited' : formatNumber(plan.events_limit)}
                        </div>
                        <div className="text-sm text-muted-foreground">events per month</div>
                      </div>
                      
                      <ul className="space-y-2 flex-1 mb-4">
                        {plan.features.map((feature, index) => (
                          <li key={index} className="flex items-center gap-2 text-sm">
                            <Check className="h-4 w-4 text-green-500" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      
                      <div className="mt-auto">
                        <Button
                          className="w-full"
                          onClick={() => planKey === 'enterprise' ? window.open('mailto:sales@useturret.com?subject=Enterprise%20Plan%20Inquiry', '_blank') : handleUpgrade(planKey)}
                          disabled={upgrading === planKey}
                        >
                          {upgrading === planKey ? 'Processing...' : 
                           planKey === 'enterprise' ? 'Contact Sales' : 
                           organization.trial_active ? 'Subscribe' : 'Change Plan'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 